import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip, IconButton } from '@material-ui/core';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import { setOpenSnackbar } from '../../../store/slices/snackbarSlice';
import useVrackApi from '../../../hooks/useVrackApi';

const PowerStatus = {
  DISABLED: 'disabled',
  RUNNING: 'running',
  STOPPED: 'stopped'
};

SetRackLRUStateButton.propTypes = {
  rackDetails: PropTypes.object.isRequired,
  jobInProgress: PropTypes.bool.isRequired,
  lru: PropTypes.object.isRequired,
  sx: PropTypes.array
};

export default function SetRackLRUStateButton({ rackDetails, jobInProgress, lru, sx = [] }) {
  const dispatch = useDispatch();

  const [lruPower, setLruPower] = useState(PowerStatus.DISABLED);
  const { fetchData: setLRUStateApi } = useVrackApi('post', '/racks/lrus/running');
  const [error, setError] = useState(null);
  const identity = useSelector((state) => state.identity.value);
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));
  const getDesiredPowerState = () => (lruPower === PowerStatus.RUNNING ? 'off' : 'on');
  const getDesiredRunningState = () => lruPower !== PowerStatus.RUNNING;

  useEffect(() => {
    if (rackDetails.running?.state !== PowerStatus.RUNNING) {
      setLruPower(PowerStatus.DISABLED);
    } else if (lru.running?.state !== PowerStatus.STOPPED) {
      setLruPower(PowerStatus.RUNNING);
    } else {
      setLruPower(PowerStatus.STOPPED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, rackDetails]);

  const getButtonColor = () => {
    if (lruPower === PowerStatus.RUNNING) {
      return 'success';
    }
    return 'secondary';
  };

  const handlePowerToggle = async () => {
    try {
      const payload = {
        environment: rackDetails.region,
        identity,
        releaseName: rackDetails.name,
        lrus: [
          {
            type: lru.type,
            units: [lru.unit]
          }
        ],
        running: getDesiredRunningState()
      };
      await setLRUStateApi({ data: payload });
      openSnackbar({
        message: `Request to set rack LRU running state sent successfully!`
      });
    } catch (error) {
      openSnackbar({
        message: error?.response?.data?.message || 'Something went wrong',
        severity: 'error'
      });
      setError(error);
    }
  };

  return (
    <Tooltip title={`Turn ${getDesiredPowerState()} LRU`} placement="top">
      <div>
        <IconButton
          size="small"
          disabled={jobInProgress || lruPower === PowerStatus.DISABLED}
          onClick={handlePowerToggle}
          sx={{ ...sx }}
          color={getButtonColor()}
        >
          <PowerSettingsNewOutlinedIcon />
        </IconButton>
      </div>
    </Tooltip>
  );
}
